import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ExternalLink } from "lucide-react";
import { API } from "../../config";
import { FUNNELS } from "../../data/funnelContent";
import { CONGRATS } from "../../data/congratsFunnel";
import { WEB_FUNNELS } from "../../data/webDesignFunnel";
import { FunnelLossAlert } from "./FunnelLossAlert";

const PERSONAS = ["obava", "analytik", "jednoducho"];
const KNOWN = [
  ...FUNNELS.map((f, i) => ({ page: `/riesenia/${f.slug}`, label: `${i + 1}. ${f.label}` })),
  ...CONGRATS.map((c) => ({ page: c.path, label: c.label })),
  ...WEB_FUNNELS.map((c) => ({ page: c.path, label: c.label })),
  { page: "/vsetko-v-jednom-2026", label: "Ponuka Všetko v jednom 2026" },
  ...PERSONAS.map((p) => ({ page: `/pre-firmy/${p}`, label: `Persona firmy · ${p}` })),
  ...PERSONAS.map((p) => ({ page: `/pre-agentury/${p}`, label: `Persona agentúry · ${p}` })),
  { page: "/bezpecnost-webu", label: "Bezpečnosť webu" },
  { page: "/sluzby", label: "Služby (hub)" },
];
const EMPTY = { visitors: 0, order_clicks: 0, orders: 0, revenue_eur: 0, ad_spend_eur: 0, cvr_pct: 0, cost_per_order_eur: null, roas: null, profit_eur: 0 };
const eur = (v) => (v == null ? "—" : `€${Number(v).toLocaleString("sk-SK", { maximumFractionDigits: 2 })}`);
const headers = () => ({ "X-Admin-Token": localStorage.getItem("admin_token") || "" });
const thisMonth = () => new Date().toISOString().slice(0, 7);

const SpendInput = ({ value, onSave, testid }) => {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  return (
    <input type="number" min="0" step="0.01" value={v} placeholder="0" onChange={(e) => setV(e.target.value)}
      onBlur={() => Number(v || 0) !== Number(value || 0) && onSave(Number(v || 0))}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className="w-24 px-2 py-1 text-right border border-gray-300 rounded bg-white text-gray-900 placeholder-gray-400" data-testid={testid} />
  );
};

const Kpi = ({ label, value, testid, tone = "text-gray-900" }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4">
    <p className="text-[11px] uppercase text-gray-500">{label}</p>
    <p className={`text-2xl font-black ${tone}`} data-testid={testid}>{value}</p>
  </div>
);

export default function AdminFunnelSummaryTab() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/admin/funnels/summary`, { params: { month }, headers: headers() })).data); }
    catch { toast.error("Súhrn sa nepodarilo načítať"); }
  }, [month]);
  useEffect(() => { setData(null); load(); }, [load]);

  const save = async (page, amount) => {
    try { await axios.put(`${API}/admin/funnels/ad-spend`, { month, page, amount_eur: amount }, { headers: headers() }); toast.success("Cena reklamy uložená"); load(); }
    catch { toast.error("Uloženie zlyhalo"); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  const byPage = Object.fromEntries(data.rows.map((r) => [r.page, r]));
  const extra = data.rows.filter((r) => !KNOWN.some((k) => k.page === r.page)).map((r) => ({ page: r.page, label: r.page }));
  const rows = [...KNOWN, ...extra].map((k) => ({ ...EMPTY, ...k, ...(byPage[k.page] || {}) }));
  const t = data.totals;

  return (
    <div className="space-y-5" data-testid="admin-funnel-summary-tab">
      <FunnelLossAlert />
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600">Mesiac</label>
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded bg-white text-gray-900" data-testid="funnel-summary-month" />
        <p className="text-xs text-gray-500">Cenu reklamy zadajte pri funneli a potvrďte Enterom. Objednávky a tržby sa počítajú zo zaplatených platieb, ktoré začali na danej stránke.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Návštevníci" value={t.visitors} testid="funnel-total-visitors" />
        <Kpi label="Objednávky" value={t.orders} testid="funnel-total-orders" />
        <Kpi label="Tržby" value={eur(t.revenue_eur)} testid="funnel-total-revenue" />
        <Kpi label="Reklama" value={eur(t.ad_spend_eur)} testid="funnel-total-spend" />
        <Kpi label="Cena za objednávku" value={eur(t.cost_per_order_eur)} testid="funnel-total-cpa" />
        <Kpi label="Zisk" value={eur(t.profit_eur)} testid="funnel-total-profit" tone={t.profit_eur >= 0 ? "text-emerald-600" : "text-red-600"} />
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]" data-testid="funnel-summary-table">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-3">Funnel</th><th className="px-3 py-3 text-right">Návštevníci</th><th className="px-3 py-3 text-right">Kliky</th>
              <th className="px-3 py-3 text-right">Objednávky</th><th className="px-3 py-3 text-right">Konverzia</th><th className="px-3 py-3 text-right">Tržby</th>
              <th className="px-3 py-3 text-right">Cena reklamy</th><th className="px-3 py-3 text-right">Cena/objednávku</th><th className="px-3 py-3 text-right">ROAS</th><th className="px-3 py-3 text-right">Zisk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.page} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`funnel-summary-row-${r.page}`}>
                <td className="px-3 py-2"><a href={r.page} target="_blank" rel="noreferrer" className="font-bold text-gray-900 hover:underline inline-flex items-center gap-1">{r.label} <ExternalLink className="w-3 h-3 text-gray-400" /></a><p className="text-[11px] text-gray-400 font-mono">{r.page}</p></td>
                <td className="px-3 py-2 text-right">{r.visitors}</td>
                <td className="px-3 py-2 text-right">{r.order_clicks}</td>
                <td className="px-3 py-2 text-right font-bold">{r.orders}</td>
                <td className="px-3 py-2 text-right">{r.cvr_pct} %</td>
                <td className="px-3 py-2 text-right font-bold">{eur(r.revenue_eur)}</td>
                <td className="px-3 py-2 text-right"><SpendInput value={r.ad_spend_eur} onSave={(v) => save(r.page, v)} testid={`funnel-spend-${r.page}`} /></td>
                <td className="px-3 py-2 text-right">{eur(r.cost_per_order_eur)}</td>
                <td className="px-3 py-2 text-right">{r.roas == null ? "—" : `${r.roas}×`}</td>
                <td className={`px-3 py-2 text-right font-bold ${r.profit_eur > 0 ? "text-emerald-600" : r.profit_eur < 0 ? "text-red-600" : "text-gray-400"}`}>{eur(r.profit_eur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500">ROAS = tržby ÷ cena reklamy (nad 3× je zdravé). Konverzia = objednávky ÷ návštevníci. Objednávky pred touto verziou nemajú uloženú stránku, preto sa nezapočítajú.</p>
    </div>
  );
}
